import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { askAssistant } from "@/lib/api";

const suggestions = [
  "What's the best package for a 50-guest beach wedding?",
  "How much for 30 guests on a boat party?",
  "Do you service Noosa and Caloundra?",
  "How far in advance should I book?",
];

const sessionId = "assistant-" + Math.random().toString(36).slice(2);

export default function Assistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "G'day! I'm your Beachside Bartending concierge 🌊 Ask me about packages, pricing, service areas or planning your event — I'm here to help." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { reply } = await askAssistant({ session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I'm having a moment. Please try again or send an enquiry via the contact form." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen flex flex-col">
      <Nav solid />
      <section className="flex-1 max-w-4xl w-full mx-auto px-5 md:px-8 pt-32 md:pt-36 pb-8 flex flex-col">
        <div className="mb-6">
          <p className="overline text-xs text-coral mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Booking Assistant</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ocean-deep leading-[1.02] tracking-tight">Ask me anything.</h1>
        </div>

        <div className="flex-1 rounded-3xl bg-white border border-sand p-5 md:p-6 flex flex-col min-h-[52vh]">
          <div className="flex-1 overflow-y-auto chat-scroll space-y-4 pr-1" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-5 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-ocean-deep text-white rounded-br-sm" : "bg-seafoam text-ocean-deep rounded-bl-sm"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start"><div className="bg-seafoam text-ocean px-5 py-3 rounded-2xl rounded-bl-sm"><Loader2 className="w-4 h-4 animate-spin" /></div></div>
            )}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 pt-4">
              {suggestions.map((s) => (
                <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="text-xs text-ocean border border-sand rounded-full px-4 py-2 hover:border-coral hover:text-coral transition-colors">{s}</button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-3 pt-4 mt-4 border-t border-sand">
            <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your message..." className="flex-1 h-12 px-5 rounded-full bg-cream border border-sand focus:border-coral outline-none text-sm text-ocean-deep" />
            <button type="submit" data-testid="chat-send" disabled={loading} className="w-12 h-12 rounded-full bg-coral text-white grid place-items-center hover:bg-[#c9633f] transition-colors disabled:opacity-60">
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
